import { Link } from "@tanstack/react-router";
import {
  Star,
  MapPin,
  Heart,
  ChevronLeft,
  ChevronRight,
  Users,
  DoorOpen,
  Sparkles,
  BookmarkCheck,
} from "lucide-react";
import { formatNaira, getStartingPrice, hasGuestRating, type Venue } from "@/data/venues";
import { useFavorites } from "@/context/FavoritesContext";
import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";

export function VenueCard({ venue }: { venue: Venue }) {
  const { isFavorite, toggle } = useFavorites();
  const saved = isFavorite(venue.id);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, dragFree: false });
  const [activeSlide, setActiveSlide] = useState(0);

  const scrollPrev = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollPrev();
    },
    [emblaApi],
  );

  const scrollNext = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollNext();
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveSlide(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  const images = Array.from(new Set([venue.image, ...venue.gallery].filter(Boolean))).slice(0, 5);
  const startingPrice = getStartingPrice(venue);
  const hasRoomTypes = venue.roomTypes && venue.roomTypes.length > 0;
  const roomTypeCount = venue.roomTypes?.length ?? 0;
  const totalGuests =
    venue.capacity ??
    venue.roomTypes?.reduce((max, room) => Math.max(max, room.maxGuests || 0), 0) ??
    0;
  const description = venue.description?.trim();

  return (
    <div className="group/card relative flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
      {/* Image carousel */}
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-accent/30">
        <div ref={emblaRef} className="size-full overflow-hidden">
          <div className="flex size-full">
            {images.map((img, i) => (
              <Link
                key={i}
                to="/venues/$venueId"
                params={{ venueId: venue.id }}
                className="relative min-w-0 flex-[0_0_100%]"
              >
                <img
                  src={img}
                  alt={`${venue.name} — photo ${i + 1}`}
                  loading="lazy"
                  className="size-full object-cover"
                />
              </Link>
            ))}
          </div>
        </div>

        {/* Carousel navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={scrollPrev}
              aria-label="Previous photo"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 grid size-8 place-items-center rounded-full bg-white/90 shadow-md opacity-100 transition-all hover:bg-white hover:scale-110 lg:opacity-0 lg:group-hover/card:opacity-100"
            >
              <ChevronLeft className="size-4 text-gray-800" />
            </button>
            <button
              onClick={scrollNext}
              aria-label="Next photo"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 grid size-8 place-items-center rounded-full bg-white/90 shadow-md opacity-100 transition-all hover:bg-white hover:scale-110 lg:opacity-0 lg:group-hover/card:opacity-100"
            >
              <ChevronRight className="size-4 text-gray-800" />
            </button>
          </>
        )}

        {/* Carousel dots */}
        {images.length > 1 && (
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1">
            {images.map((_, i) => (
              <span
                key={i}
                className={`block rounded-full transition-all duration-300 ${activeSlide === i ? "h-[5px] w-3 bg-white" : "size-[5px] bg-white/60"}`}
              />
            ))}
          </div>
        )}

        {/* Heart / save button */}
        <button
          aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle(venue.id);
          }}
          className="absolute right-3 top-3 z-10 transition-transform hover:scale-110 active:scale-95"
        >
          <Heart
            className={`size-6 drop-shadow-md transition-colors ${saved ? "fill-primary text-primary" : "fill-black/25 text-white stroke-[1.5]"}`}
          />
        </button>

        {/* Facility badge */}
        <span className="absolute left-3 top-3 rounded-lg bg-white/95 px-2 py-0.5 text-[11px] font-semibold text-foreground shadow-sm backdrop-blur-sm">
          {venue.facility}
        </span>
      </div>

      {/* Venue info */}
      <Link
        to="/venues/$venueId"
        params={{ venueId: venue.id }}
        className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-4"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <h3 className="font-semibold text-base leading-snug text-foreground line-clamp-2 sm:text-lg">
            {venue.name}
          </h3>
          <div className="self-start rounded-3xl bg-white/95 px-3 py-2 text-right shadow-sm">
            {hasGuestRating(venue) ? (
              <div className="flex items-center justify-end gap-1 text-sm font-semibold text-foreground">
                <Star className="size-4 fill-primary text-primary" /> {venue.rating.toFixed(1)}
              </div>
            ) : (
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">New</div>
            )}
          </div>
        </div>

        <p className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="size-4" /> {venue.location}, Oyo State
        </p>

        {description && (
          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
            {description}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {hasRoomTypes && (
            <span className="rounded-full border border-border/70 bg-accent/70 px-3 py-1 text-xs font-semibold text-foreground">
              {roomTypeCount} room type{roomTypeCount !== 1 ? "s" : ""}
            </span>
          )}
          {totalGuests > 0 && (
            <span className="rounded-full border border-border/70 bg-accent/70 px-3 py-1 text-xs font-semibold text-foreground">
              <Users className="size-3 inline-block" /> {totalGuests} guest
              {totalGuests !== 1 ? "s" : ""}
            </span>
          )}
          {venue.bookingCount > 0 && (
            <span className="rounded-full border border-border/70 bg-accent/70 px-3 py-1 text-xs font-semibold text-foreground">
              <BookmarkCheck className="size-3 inline-block" /> {venue.bookingCount} booking
              {venue.bookingCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {venue.amenities && venue.amenities.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {venue.amenities.slice(0, 3).map((amenity) => (
              <span
                key={amenity}
                className="inline-flex items-center gap-1 rounded-md bg-background px-1.5 py-0.5 text-[10px] font-medium text-foreground border border-border/40"
              >
                <Sparkles className="size-2.5 text-primary" />
                {amenity}
              </span>
            ))}
          </div>
        )}

        <p className="mt-auto border-t border-border/50 pt-3">
          {hasRoomTypes && <span className="text-[11px] text-muted-foreground">From </span>}
          <span className="font-display text-base font-bold text-foreground sm:text-lg">{formatNaira(startingPrice)}</span>
          <span className="text-sm text-muted-foreground">
            {" "}
            / {venue.category === "hotel" ? "night" : "event"}
          </span>
        </p>
      </Link>
    </div>
  );
}

